import { Injectable } from '@angular/core';
import { Observable, Subject, forkJoin } from 'rxjs';

import * as _ from 'underscore';

import { PersonSearchService } from './person-search.service';
import { PersonInsightsData } from '../person-search-insights/PersonInsightsData';
import { AggregationRequest } from '../common-interfaces-classes/AggregationRequest';
import { CallStage } from '../enums/CallStage';
import { AggReqObjectHandlerService } from './agg-req-object-handler.service';
import { FilterDataHandlerService } from './filter-data-handler.service';
import { InsightsDataService } from './insights-data.service';
import { ExtraInfo } from '../person-search-filter/ExtraInfo';
import { LoaderEventsService } from './loader-events.service';
import { LoaderType } from '../enums/LoaderType';
import PersonSearchResponse from '../common-interfaces-classes/PersonSearchResponse';
import { LocalStorageService } from './local-storage.service';
import { SessionStorageService } from './session-storage.service';
import { SimilarCandidateService } from './similar-candidate.service';
import { FilterParams } from '../common-interfaces-classes/FilterParams';
import { GeneralConstants } from '../constants/GeneralConstants';
import { ActivatedRoute } from '@angular/router';
import { retry } from 'rxjs/operators';
import { TalentInsightsReqHandlerService } from './talent-insights-req-handler.service';
import { RedirectData } from '../common-interfaces-classes/RedirectData';
import { JdDataService } from '../jd-data.service';
import { SimilarCandidateDetails } from '../common-interfaces-classes/SimilarCandidate';

type StatusMap = {
  jobTitleStatus: string;
  companyStatus: string;
  industryStatus: string;
  yearsOfExperienceType: string;
};

@Injectable({
  providedIn: "root",
})
export class AggregationService {

  constructor(
    private personSearchService: PersonSearchService,
    private aggReqObjHandler: AggReqObjectHandlerService,
    private filterDataHandlerService: FilterDataHandlerService,
    private insightsDataService: InsightsDataService,
    private loaderEventService: LoaderEventsService,
    private localStorageService: LocalStorageService,
    private similarCandidateService: SimilarCandidateService,
    private activatedRoute: ActivatedRoute,
    private talentInsightsReqHandlerService: TalentInsightsReqHandlerService,
    private sessionStorageService: SessionStorageService,
    private jdDataService: JdDataService,
    private route: ActivatedRoute
  ) {}

  private statsSubject: Subject<[any, any, any]> = new Subject();
  private changedJobSubject: Subject<any> = new Subject();
  private changedJobResetSubject: Subject<any> = new Subject();


  get statsUpdateEvent(): Observable<[any, any, any]> {
    return this.statsSubject.asObservable();
  }

  get changedJobStatsEvent(): Observable<any> {
    return this.changedJobSubject.asObservable();
  }

  changedJobStats(statsObject){
    this.changedJobSubject.next(statsObject);
  }

  get setResetChangedJob(): Observable<any> {
    return this.changedJobResetSubject.asObservable();
  }

  changeJobResetEmit(){
    this.changedJobResetSubject.next('reset');
  }

  emitStatsObject(statsObject: [any, any, any]) {
    this.statsSubject.next(statsObject);
  }

  private insightsDataSubject: Subject<any> = new Subject();

  get insightsDataEvent(): Observable<any> {
    return this.insightsDataSubject.asObservable();
  }

  emitInsightsObject(insightsData: any) {
    this.insightsDataSubject.next(insightsData);
  }

  private recCandidateDataSubject: Subject<PersonSearchResponse> = new Subject();

  get recCandidateDataEvent(): Observable<PersonSearchResponse> {
    return this.recCandidateDataSubject.asObservable();
  }

  get geoIpLocation() {
    const location = JSON.parse(this.localStorageService.getItem('userGeoIpLocation'));
    const country = location?.country?.toLowerCase();
    const city = location?.city?.toLowerCase();
    const region = location?.region?.toLowerCase();
    const continent = location?.continent?.toLowerCase();
    return { location: `${city || ''}${city && (region || country) ? ', ' : ''}${region || ''}${region && country ? ', ' : ''}${country || ''}`, continent } 
  }

  emitRecCandidateObject(recCandidateData: PersonSearchResponse) {
    this.recCandidateDataSubject.next(recCandidateData);
  }

  processAggregationResponse(
    pIData: PersonInsightsData,
    resp: any,
    reqObj: AggregationRequest
  ) {
    const response = Object.assign({}, resp);
    const personInsightsData = Object.assign({}, pIData);
    const localReqObj = Object.assign({}, reqObj);
    if (localReqObj.aggregatetype === "gender") {
      personInsightsData.dataSourceGender =
        this.createDataSourceGender(response);
    } else if (localReqObj.aggregatetype === "currentyearsofexperience") {
      personInsightsData.dataSourceExpInCurrentRole =
        this.createExpInCurrentRole(response);
    } else if (
      localReqObj.aggregatetype === "totalyearsofexperience" ||
      localReqObj.aggregatetype === "averageyearsofexperience"
    ) {
      personInsightsData.dataSourceCurrentExperience =
        this.createCurrentExperience(response, localReqObj.aggregatetype);
    } else if (localReqObj.aggregatetype === "seniority") {
      personInsightsData.dataSourceSeniority =
        this.createSeniorityData(response);
    } else if (localReqObj.aggregatetype === "location") {
      personInsightsData.topLocations = response?.location;
      personInsightsData.dataSourceWorkCountry =
        this.createWorkCountryData(response);
    } else if (
      localReqObj.aggregatetype === "current_company" ||
      localReqObj.aggregatetype === "company" ||
      localReqObj.aggregatetype === "pastcompany" 
    ) {
      personInsightsData.dataSourceCurrentCompany = this.createCompanyData(
        !!response[localReqObj.aggregatetype]
          ? response[localReqObj.aggregatetype].slice(0, 5)
          : []
      );
    } else if (
      localReqObj.aggregatetype === "jobtitle" ||
      localReqObj.aggregatetype === "current_jobtitle" ||
      localReqObj.aggregatetype === "pastjobtitle"
    ) {
      personInsightsData.titles = response[localReqObj.aggregatetype]?.slice(0, 5);
    } else if (localReqObj.aggregatetype === "school") {
      personInsightsData.school =
        response[localReqObj.aggregatetype]?.slice(0, 5) || [];
    } else if (localReqObj.aggregatetype === "skills") {
      personInsightsData.skills =
        response[localReqObj.aggregatetype]?.slice(0, 5) || [];
    } else if (localReqObj.aggregatetype === "fieldofstudy") {
      personInsightsData.fieldOfStudy =
        response[localReqObj.aggregatetype]?.slice(0, 5) || [];
    } else if (
      localReqObj.aggregatetype === "current_industry" ||
      localReqObj.aggregatetype === "industry" ||
      localReqObj.aggregatetype === "pastindustry" 
    ) {
      personInsightsData.currentIndustry =
        response[localReqObj.aggregatetype]?.slice(0, 5) || [];
    }
    return personInsightsData;
  }

  getStatsAggs() {
    forkJoin([
      // this.getAggregationReport("likelytomove"),
      this.getAggregationReport("total_tenure_median"),
      this.getAggregationReport("gender"),
      this.getAggregationReport("changedjobs")
    ]).subscribe(res => this.emitStatsObject(res));
  }

  getChangedJobStats(start, end, changedJobStatus){
    const obj = {
      endDuration: end,
      changedjobsstatus: 'yes' // For aggregate type: changed jobs, then value of changedjobs is always true
    }
    this.getAggregationReport("changedjobs", obj).subscribe(res => this.changedJobStats(res));
  }

  private getAggregationReport(aggregatetype: string, obj?: any ) {
    // const localRequestObject = this.aggReqObjHandler.getSharedRequestObject();
    const localRequestObject = this.getRequestWithFilters();
    Object.assign(localRequestObject, {
      resultsize: 1,
      aggregatetype: aggregatetype
    });
    if ((aggregatetype === "changedjobs") && obj && !_.isEmpty(obj)) {
      Object.assign(localRequestObject, {
        // changedjobsstartdurationinmonths: obj?.statrDuration,
        changedjobsenddurationinmonths: obj?.endDuration,
      });
    }
    if ((aggregatetype === "changedjobs")) {
      Object.assign(localRequestObject, {
        // changedjobsstartdurationinmonths: obj?.statrDuration,
        changedjobs: 'yes'
      });
    }
    delete localRequestObject["talentdiscoveryhistoryid"];
    const type = this.route.snapshot.queryParams['type'];
    const searchSource = this.route.snapshot.queryParams['searchSource'];
    const searchKeyword = this.route.snapshot.queryParams['keyword'];

    if(this.similarCandidateService?.isListingCalledFromSimilarCandidate || type == 'quick-search' || ((searchSource && searchSource == 'history-component') && searchKeyword))
      localRequestObject["calledfrom"] = "listing";
    else
      delete localRequestObject["calledfrom"]
    delete localRequestObject["input"];
    delete localRequestObject["typeahead"];
    return this.personSearchService.getTotalAggs(localRequestObject);
  }

  /**
   * 
   *  Before calling this method make sure to update the filterData object from filter component or any other component that might be calling this method if applicable.
   *  Once we get the response, we will call the getTotalAggs if insights tab is active.
   * 
   */
  getPersonSearchRecommendedCandidates(extraInfo : ExtraInfo) {
    this.loaderEventService.emitLoaderObject(LoaderType.RECOMMENDED_CANDIDATE);
    const request = this.getRequestWithFilters(extraInfo);
    delete request.aggregatetype;
    this.aggReqObjHandler.setShareRequestObject(request);
    this.personSearchService.getPersonSearchRecommendCandidates(request).subscribe(response => {
      const candidateObject = new PersonSearchResponse();
      if (response.status == 'success') {
        candidateObject.candidateList = response?.data?.results?.length ? response.data.results.map(c => ({ ...c, selected: false })) : [];
        candidateObject.totalCandidatesCount = response.data?.totalresultscount;
        candidateObject.talentdiscoveryhistoryid = response?.data?.talentdiscoveryhistoryid;
        candidateObject.totalresultscount = response?.data?.totalresultscount;
        candidateObject.booleanquery = response?.data?.booleanquery;
      }
      this.emitRecCandidateObject(candidateObject);
    });
  }

  /**
   * 
   * This method will return redirect data if redirect from talent or company insights
   * 
   */
  getRedirectDataV2() : ExtraInfo {
    const extraInfo = new ExtraInfo();
    extraInfo.redirectSource = this.activatedRoute.snapshot.queryParams['redirectSource'];
    extraInfo.redirectData = extraInfo.redirectSource == 'talent-insights' ? this.getTalentInsightsMandatoryData(): this.getCompanyInsightsMandatoryData();
    if (extraInfo?.redirectData?.additionalFilters) {
      Object.keys(extraInfo.redirectData.additionalFilters).forEach(filter => {
        Object.assign(extraInfo.redirectData, { [filter]: extraInfo.redirectData.additionalFilters[filter] });
      });
    }
    return extraInfo;
  }
  

  getRedirectData() : ExtraInfo {
    const extraInfo = new ExtraInfo();
    extraInfo.redirectSource = this.activatedRoute.snapshot.queryParams['redirectSource'];

    const talentFilters = this.localStorageService.getItem('talentfilters');
    const companyFilters = this.localStorageService.getItem('companyinsightsdetails');
    if (extraInfo.redirectSource === "talent-insights" && !!talentFilters) {
      extraInfo.redirectData = JSON.parse(talentFilters);
    } else if (extraInfo.redirectSource === "company-insights" && !!companyFilters) {
      extraInfo.redirectData = JSON.parse(companyFilters);
    } else {
      extraInfo.redirectData = null;
    }
    if (extraInfo?.redirectData?.additionalFilters) {
      Object.keys(extraInfo.redirectData.additionalFilters).forEach(filter => {
        Object.assign(extraInfo.redirectData, { [filter]: extraInfo.redirectData.additionalFilters[filter] });
      });
    }

    return extraInfo;
  }

  /**
   * 
   * TODO : pick all status map data from filter data object. if time permits remove status map param
   * 
   * @param extraInfo 
   * @param statusMap 
   * @returns {AggregationRequest}
   */
  getRequestWithFilters(extraInfo?: ExtraInfo) {
    const request = this.aggReqObjHandler.getSharedRequestObject();
    let filterData = this.filterDataHandlerService.fetchFilterData;
    const statusMap = this.getStatusMap(filterData);   
    if (!extraInfo) extraInfo = (this.filterDataHandlerService.isRedirectFromTalentInsightsPage || this.filterDataHandlerService.isRedirectFromCompanyInsightsPage) ? this.getRedirectDataV2() : this.getRedirectData(); 
    const redirectDataTI = extraInfo?.redirectSource == 'talent-insights' ? this.getTalentInsightsRedirectDataV2(): this.getCompanyInsightsRedirectDataV2();
    const jdFilterData = extraInfo.calledFrom === GeneralConstants.COMPONENT_NAME.SC_MODAL ? 
      extraInfo.redirectData : 
      this.jdDataService.jdDataFromSession;

    this.applyFilters(request, filterData, extraInfo, redirectDataTI, statusMap);

    // history id
    request.talentdiscoveryhistoryid = filterData.talentdiscoveryhistoryid;

    //Continent
    if (
      this.talentInsightsReqHandlerService.isTalentInsightsTab ||
      this.filterDataHandlerService.isRedirectFromTalentInsightsPage
    ) {
      request.continentstobeincluded = [this.localStorageService.getItem('talentRegion')];
    } else if (this.similarCandidateService.isListingCalledFromSimilarCandidate || this.filterDataHandlerService.isRedirectFromCompanyInsightsPage){ 
      request.continentstobeincluded = request.continentstobeincluded;
    } else if(this.talentInsightsReqHandlerService.isCompanyInsightsTab) {
      delete request.continentstobeincluded;
    } else if (this.jdDataService.isComingFromJobDescription) {
      request.continentstobeincluded = extraInfo?.redirectData?.continentstobeincluded ? 
        [extraInfo.redirectData.continentstobeincluded] : [this.localStorageService.getItem("similar-region")];
    } else{
      request.continentstobeincluded = [this.sessionStorageService.getItem("historyRegion") ? this.sessionStorageService.getItem("historyRegion") : this.localStorageService.getItem("region")];
    }

    request.locations = filterData.locationSelectedList && filterData.locationSelectedList.length ? filterData.locationSelectedList.filter(l => l.isExcluded == false).map(l => l.key) : this.geoIpLocation && request.continentstobeincluded && request.continentstobeincluded.length && this.geoIpLocation.continent == request.continentstobeincluded[0] ? [`${this.geoIpLocation.location}.iplocation`] : [];

    // Handling all the mandatory fields
    this.applyMandatoryFilters(request, extraInfo, redirectDataTI, filterData);

    // Handling all the request insights filters
    // Replace new FilterParams with company insights redirect data later
    this.applyInsightsFilters(request, extraInfo, redirectDataTI);

    if (
      (this.filterDataHandlerService.isRedirectFromTalentInsightsPage ||
      this.filterDataHandlerService.isReportDetailsPage ||
      this.filterDataHandlerService.isRedirectFromCompanyInsightsPage) &&
      !!request.calledfrom
    ) {
      delete request.calledfrom;
    }

    if (this.filterDataHandlerService.isPersonSearchListingPage && !this.similarCandidateService.isListingCalledFromSimilarCandidate) {
      request.needcuratedtalentpooltags = true;
    }

    const searchType = this.activatedRoute.snapshot.queryParams['type'];
    if(searchType == 'quick-search') {
      request.calledfrom = 'listing';
      request.startindex = 0;
      request.resultsize = 20;
      request.searchviaboolean =  true;
    }

    if (this.jdDataService.isComingFromJobDescription) {
      request.searchedfrom = GeneralConstants.REDIRECT_SOURCES.JOB_DESCRIPTION;
      request.job_description_keys = this.jdDataService.getJdKeys(jdFilterData);
    }

    if (extraInfo?.redirectData?.comingFromSC) { 
      this.similarCandidateService.modifyRequestForSCCount(request, extraInfo?.redirectData);
    }

    return request;
  }

  private applyInsightsFilters(request: AggregationRequest, extraInfo: ExtraInfo, redirectDataTI: FilterParams) {
    if (extraInfo?.redirectData?.comingFromSC) 
      return;

    request.insightslocations = this.getInsightsLocation(extraInfo, redirectDataTI, new FilterParams());
    request.insightscompanies = this.getInsightsCompanies(extraInfo, redirectDataTI, new FilterParams());
    request.insightsindustries = this.getInsightsIndustries(extraInfo, redirectDataTI, new FilterParams());
    request.insightsschools = this.getInsightsSchools(extraInfo, redirectDataTI, new FilterParams());
    request.insightseducationalmajors = this.getInsightsEducationalMajors(extraInfo, redirectDataTI, new FilterParams());
    request.insightsjobtitles = this.getInsightsJobTitles(extraInfo, redirectDataTI, new FilterParams());
    request.insightspastjobtitles = this.getInsightsPastJobTitles(extraInfo, redirectDataTI, new FilterParams());
    request.insightspreferredskills = this.getInsightsPrefferdedSkills(extraInfo, redirectDataTI, new FilterParams());
    request.insightsfunctions = this.getInsightsFunctions(extraInfo, redirectDataTI, new FilterParams());
    request.insightseducationaldegrees = this.getInsightsEducationalDegrees(extraInfo, redirectDataTI, new FilterParams());
    request.insightsseniority = this.getInsightsSeniority(extraInfo, redirectDataTI, new FilterParams());
    request.lastjobcompanies = this.getLastJobCompanies(extraInfo, redirectDataTI, new FilterParams());

    if (this.manadatoryApplicable(extraInfo) && extraInfo.redirectData) {
      request.recentgraduatesonly = extraInfo.redirectData?.recentgraduatesonly;
      request.recentcompanyhires = extraInfo.redirectData?.recentcompanyhires;
    }

    if (extraInfo.redirectSource === 'talent-insights' && extraInfo.redirectData) {
      request.recentcompanyattrition = extraInfo.redirectData?.recentcompanyattrition;
    }
  }

  private applyMandatoryFilters(request: AggregationRequest, extraInfo: ExtraInfo, redirectDataTI: FilterParams, filterData: FilterParams) {
    if (extraInfo?.redirectData?.comingFromSC) 
      return;

    request.mandatoryskills = this.getMandatorySkills(extraInfo, redirectDataTI, new FilterParams(), filterData);
    request.mandatoryjobtitles = this.getMandatoryJobTitle(extraInfo, redirectDataTI, new FilterParams(), filterData);
    this.manadatoryApplicable(extraInfo) && extraInfo?.redirectData?.mandatoryjobtitles?.length ?
      filterData.jobTitleSelectedList.filter(s => !!s.isMandatory).map(s => s.key).concat(extraInfo?.redirectData?.mandatoryjobtitles) :
      filterData.jobTitleSelectedList.filter(s => !!s.isMandatory).map(s => s.key);
    request.mandatorylocations = this.manadatoryApplicable(extraInfo) && this.getMandatoryLocations(extraInfo);
    request.mandatorycompanies = this.manadatoryApplicable(extraInfo) && this.getMandatoryCompanies(extraInfo);
    request.mandatoryindustries = this.manadatoryApplicable(extraInfo) && this.getMandatoryIndustries(extraInfo);
    request.mandatoryschools = this.manadatoryApplicable(extraInfo) && this.getMandatorySchools(extraInfo);;
    request.mandatoryfunctions = this.manadatoryApplicable(extraInfo) && extraInfo?.redirectData?.mandatoryfunctions?.length ? extraInfo?.redirectData?.mandatoryfunctions : [];
    request.mandatoryeducationalmajors = this.manadatoryApplicable(extraInfo)
      && this.getMandatoryEducationalMajors(extraInfo);
    request.mandatoryeducationaldegrees = this.manadatoryApplicable(extraInfo)
      && this.getMandatoryEducationalDegrees(extraInfo);
    request.mandatoryseniority = this.manadatoryApplicable(extraInfo)
      && extraInfo?.redirectData?.mandatoryseniority?.length ? extraInfo?.redirectData?.mandatoryseniority : [];
  }

  private applyFilters(request: AggregationRequest, filterData: FilterParams, extraInfo: ExtraInfo, redirectDataTI: FilterParams, statusMap: { jobTitleStatus: string; companyStatus: string; industryStatus: string; yearsOfExperienceType: any; }) {
    if (extraInfo?.redirectData?.comingFromSC) 
      return;

    request.booleanquery = this.getBooleanQuery(extraInfo, redirectDataTI, new FilterParams(), filterData);

    request.jobtitles = _.uniq(filterData.jobTitleSelectedList.filter(jobTitle => !jobTitle.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key));
    request.exclude_jobtitles = this.getExcludeJobtitles(extraInfo, redirectDataTI, new FilterParams(), filterData);
    request.jobtitletype = statusMap.jobTitleStatus;

    request.exclude_locations = this.getExcludeLocations(extraInfo, redirectDataTI, new FilterParams(), filterData);

    request.pastjobtitles = filterData.pastJobTitleSelectedList.filter(l => !l.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key);
    request.exclude_pastjobtitles = this.getExcludePastJobTitles(extraInfo, redirectDataTI, new FilterParams(), filterData);

    request.preferredskills = filterData.skillsSelectedList.filter(s => !s.isMandatory).map(s => s.isSuggested && !s.key.includes('.aisearch') ? `${s.key}.aisearch` : s.key);

    request.companies = filterData.companySelectedList.filter(company => company.isExcluded == false).map(j => j.key);
    request.exclude_companies = this.getExcludeCompanies(extraInfo, redirectDataTI, new FilterParams(), filterData);
    request.companytype = this.getCompanyStatusForTI(extraInfo, filterData);

    request.companysizes = this.getCompanySizes(extraInfo, redirectDataTI, new FilterParams(), filterData);

    request.industries = filterData.industrySelectedList.filter(industry => !industry.isExcluded).map(i => i.key);
    request.industrytype = filterData.industryStatus == CallStage.CURRENT_OR_PAST.toString() ? 'All' : filterData.industryStatus;
    request.exclude_industries = this.getExcludeIndustries(extraInfo, redirectDataTI, new FilterParams(), filterData);
    request.functions = filterData.functionSelectedList.map(l => l.key);
    request.functiontype = this.getFunctionStatusForTI(extraInfo, filterData);
    request.seniority = filterData.seniority ? _.flatten(filterData.seniority) : [];

    // Years of experience
    request.fromyearsofexperience = this.getFromYrsOfExperience(extraInfo, redirectDataTI, new FilterParams(), filterData);
    request.toyearsofexperience = this.getToYrsOfExperience(extraInfo, redirectDataTI, new FilterParams(), filterData);
    request.experiencetype =
      extraInfo?.redirectSource === 'talent-insights' && !filterData.filterAppliedOnYearsOfExperience
        ? redirectDataTI.yearsofexperience :
        filterData.yearsofexperience;

    request.currentfromyearsofexperience =
      extraInfo?.redirectSource == 'talent-insights' && !filterData.currentfromyearsofexperience
        ? !!redirectDataTI.currentfromyearsofexperience ? [redirectDataTI.currentfromyearsofexperience] : [] :
        !!filterData.currentfromyearsofexperience ? [filterData.currentfromyearsofexperience] : [];
    request.currenttoyearsofexperience =
      extraInfo?.redirectSource == 'talent-insights' && !filterData.currenttoyearsofexperience
        ? !!redirectDataTI.currenttoyearsofexperience ? [redirectDataTI.currenttoyearsofexperience] : [] :
        !!filterData.currenttoyearsofexperience ? [filterData.currenttoyearsofexperience] : [];

    // Gender
    request.genders = this.getGender(extraInfo, redirectDataTI, null, filterData);

    // Education
    request.educationaldegrees = filterData.educationSelectedList.map(e => e.key);

    // School
    request.schools = filterData.schoolSelectedList.map(s => s.key);

    // Education List
    request.schoollocation = filterData.schoolLocationSelectedList.filter(l => !l.isExcluded).map(l => l.key);

    // Exclude education location
    request.exclude_schoollocation = this.getExcludSchoolLocation(extraInfo, redirectDataTI, new FilterParams(), filterData);


    // Field Of Study
    request.educationalmajors = filterData.fieldOfStudySelectedList.map(fs => fs.key);

    // Likely To Move In
    request.likelytomovetype = filterData.candidateslikelytomove;

    // Changed jobs
    request.changedjobs = filterData.changedJobStatus;
    request.changedjobsstartdurationinmonths = filterData?.changedJobStartDuration;
    request.changedjobsenddurationinmonths = filterData?.changedJobEndDuration;

    request.fullnames = filterData?.fullnameSelectedList && filterData.fullnameSelectedList.length > 0 ? filterData.fullnameSelectedList.map(x=>x.key): [];

    if(!this.similarCandidateService.isListingCalledFromSimilarCandidate && !this.filterDataHandlerService.isRedirectFromCompanyInsightsPage && 
      !this.jdDataService.isComingFromJobDescription && !this.filterDataHandlerService.isRedirectFromTalentInsightsPage && !this.talentInsightsReqHandlerService.isCompanyInsightsTab && !this.talentInsightsReqHandlerService.isTalentInsightsTab) {
        request.distancefromselectedlocations = filterData?.distancefromselectedlocations;
        request.distancefromselectedlocationsmeasurementunit = filterData?.distancefromselectedlocationsmeasurementunit;
      } 
  }

  private getFromYrsOfExperience(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    if (extraInfo?.redirectSource === 'talent-insights' && !filterData.yearsofexperienceFrom && redirectDataTI?.yearsofexperienceFrom)
      return [redirectDataTI.yearsofexperienceFrom];

    if (filterData.yearsofexperienceFrom)
      return [filterData.yearsofexperienceFrom];
    
    return [];
  }

  private getToYrsOfExperience(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    if (extraInfo?.redirectSource === 'talent-insights' && !filterData.yearsofexperienceTo && redirectDataTI?.yearsofexperienceTo)
      return [redirectDataTI.yearsofexperienceTo];

    if (filterData.yearsofexperienceTo)
      return [filterData.yearsofexperienceTo];

    return [];
  }

  private getCompanyStatusForTI(extraInfo: ExtraInfo, filterData: FilterParams) {
    if (extraInfo?.redirectSource === 'talent-insights' && extraInfo?.redirectData?.recentcompanyattrition)
      return CallStage.PAST.toString();

    if (extraInfo?.redirectSource === 'talent-insights' && extraInfo?.redirectData?.recentcompanyhires)
      return CallStage.CURRENT.toString();

    return filterData.companyStatus == CallStage.CURRENT_OR_PAST.toString() ? "All" : filterData.companyStatus;
  }

  private getFunctionStatusForTI(extraInfo: ExtraInfo, filterData: FilterParams) {
    if (extraInfo?.redirectSource === 'company-insights')
      return CallStage.CURRENT.toString();
    else
      return 'All';
 

 //   return filterData.functionStatus == CallStage.CURRENT_OR_PAST.toString() ? "All" : filterData.functionStatus;
  }

  private getMandatoryJobTitle(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let manadatoryJobTitles = [];
    if (filterData.jobTitleSelectedList?.length) {
      manadatoryJobTitles = filterData.jobTitleSelectedList?.filter(el => !!el.isMandatory).map(el => el.key);
    }
    if (this.manadatoryApplicable(extraInfo) && extraInfo?.redirectData?.mandatoryjobtitles?.length) {
      manadatoryJobTitles = manadatoryJobTitles.concat(extraInfo?.redirectData?.mandatoryjobtitles);
    }
    if ("talent-insights" === extraInfo.redirectSource && redirectDataTI.jobTitleSelectedList?.length) {
      manadatoryJobTitles = manadatoryJobTitles.concat(redirectDataTI.jobTitleSelectedList?.filter(el => !!el.isMandatory).map(el => el.key));
    }

    return manadatoryJobTitles;
  }

  private getMandatorySkills(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let mandatorySkills = [];
    if (filterData.skillsSelectedList?.length) {
      mandatorySkills = filterData.skillsSelectedList?.filter(el => !!el.isMandatory).map(el => el.isSuggested && !el.key.includes('.aisearch') ? `${el.key}.aisearch` : el.key);
    }
    if (this.manadatoryApplicable(extraInfo) && extraInfo?.redirectData?.mandatoryskills?.length) {
      mandatorySkills = mandatorySkills.concat(extraInfo?.redirectData?.mandatoryskills);
    }
    if (("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo.redirectSource) && redirectDataTI.skillsSelectedList?.length) {
      mandatorySkills = mandatorySkills.concat(redirectDataTI.skillsSelectedList?.filter(el => !!el.isMandatory).map(el => el.key.isSuggested && !el.includes('.aisearch') ? `${el.key}.aisearch` : el.key));
    }

    return mandatorySkills;
  }

  private getMandatoryLocations(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatorylocations?.length)
      return extraInfo?.redirectData?.mandatorylocations;

    return [];
  }

  private getMandatoryCompanies(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatorycompanies?.length)
      return extraInfo?.redirectData?.mandatorycompanies;

    return [];
  }

  private getMandatoryIndustries(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatoryindustries?.length)
      return extraInfo?.redirectData?.mandatoryindustries;

    return [];
  }

  private getMandatorySchools(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatoryschools?.length)
      return extraInfo?.redirectData?.mandatoryschools;

    return [];
  }

  private getMandatoryEducationalMajors(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatoryeducationalmajors?.length)
      return extraInfo?.redirectData?.mandatoryeducationalmajors;

    return [];
  }

  private getMandatoryEducationalDegrees(extraInfo: ExtraInfo) {
    if (this.isComingFromInsights(extraInfo) && extraInfo?.redirectData?.mandatoryeducationaldegrees?.length)
      return extraInfo?.redirectData?.mandatoryeducationalmajors;

    return [];
  }

  private isComingFromInsights(extraInfo: ExtraInfo) {
    return (
      extraInfo?.redirectSource == 'talent-insights' || 
      extraInfo?.redirectSource == 'company-insights'
    );
  }

  // TODO : Use redirectDataCI when working on Company Insights
  private getInsightsLocation(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights'|| extraInfo.redirectSource === 'company-insights') && redirectDataTI?.locationSelectedList?.length) 
      return redirectDataTI?.locationSelectedList?.filter(el => !el.isExcluded).map(el => el.key);

    // if (extraInfo?.redirectSource === 'company-insights' && extraInfo?.redirectData?.locations?.length) 
    //   return extraInfo?.redirectData?.locations;
    
    return [];
  }

  private getInsightsCompanies(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if (extraInfo.redirectSource === 'talent-insights' && redirectDataTI?.companySelectedList?.length) 
      return redirectDataTI?.companySelectedList?.filter(el => !el.isExcluded).map(el => el.key);

    if (extraInfo?.redirectSource === 'company-insights' && extraInfo?.redirectData?.losingtalent) 
      return extraInfo?.redirectData?.losingtalent;

    if (extraInfo?.redirectSource === 'company-insights' && extraInfo?.redirectData?.alternative_names)
      return extraInfo?.redirectData?.alternative_names.concat(extraInfo?.redirectData?.name);

    if (extraInfo?.redirectSource === 'company-insights' && !extraInfo?.redirectData?.alternative_names)
      return [extraInfo?.redirectData?.name];

    return [];  
  }

  private getInsightsIndustries(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.industrySelectedList?.length) 
      return redirectDataTI?.industrySelectedList?.filter(el => !el.isExcluded).map(el => el.key);

    return [];
  }

  private getInsightsSchools(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.schoolSelectedList?.length) 
      return redirectDataTI?.schoolSelectedList?.map(el => el.key);

    return [];
  }

  private getInsightsEducationalMajors(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.fieldOfStudySelectedList?.length) 
      return redirectDataTI?.fieldOfStudySelectedList?.map(el => el.key);

    return [];
  }

  private getInsightsJobTitles(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.jobTitleSelectedList?.length) 
      return redirectDataTI?.jobTitleSelectedList?.filter(el => !el.isExcluded).map(el => el.key);

    return [];
  }

  private getInsightsPastJobTitles(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if (extraInfo.redirectSource === 'talent-insights' && redirectDataTI?.pastJobTitleSelectedList?.length) 
      return redirectDataTI?.pastJobTitleSelectedList?.filter(el => !el.isExcluded).map(el => el.key);

    return [];
  }

  private getInsightsPrefferdedSkills(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
   // console.log("preferred", redirectDataTI.skillsSelectedList);
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.skillsSelectedList?.length) 
    {
      console.log("preferred", redirectDataTI.skillsSelectedList);
      return redirectDataTI?.skillsSelectedList?.filter(el => !el.isMandatory).map(el => el.key);

    }


    // if (extraInfo.redirectSource === 'company-insights' && extraInfo?.redirectData?.skills?.length)
    //   return extraInfo?.redirectData?.skills;
    
    return [];
  }

  private getInsightsFunctions(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.functionSelectedList?.length) 
      return redirectDataTI?.functionSelectedList?.map(el => el.key);

    // if (extraInfo.redirectSource === 'company-insights' && extraInfo?.redirectData?.functions?.length)
    //   return extraInfo?.redirectData?.functions.filter(Boolean);

    return [];
  }

  private getInsightsEducationalDegrees(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.educationSelectedList?.length) 
      return redirectDataTI?.educationSelectedList?.map(el => el.key);

    return [];
  }

  private getInsightsSeniority(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if ((extraInfo.redirectSource === 'talent-insights' || extraInfo.redirectSource === 'company-insights') && redirectDataTI?.seniority?.length) 
      return redirectDataTI?.seniority;

    return [];
  }

  private getLastJobCompanies(extraInfo: ExtraInfo, redirectDataTI: FilterParams, redirectDataCI: FilterParams) {
    if (extraInfo.redirectSource === 'company-insights' && extraInfo?.redirectData?.winningtalent)
      return extraInfo?.redirectData?.winningtalent;

    if (
      extraInfo.redirectSource === 'company-insights' && 
      extraInfo?.redirectData?.losingtalent && 
      extraInfo?.redirectData?.alternative_names
    )
      return extraInfo?.redirectData?.alternative_names?.concat(extraInfo?.redirectData?.name);

    if (
      extraInfo.redirectSource === 'company-insights' && 
      extraInfo?.redirectData?.losingtalent && 
      !extraInfo?.redirectData?.alternative_names
    )
      return [extraInfo.redirectData?.name];
    
    return [];
  }

  private getGender(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    if (
      (extraInfo?.redirectSource === "talent-insights" || extraInfo?.redirectSource === "company-insights") && 
      ["male", "female"].includes(redirectDataTI?.gender?.toLowerCase())
    )
      return [redirectDataTI.gender];
    return filterData?.gender ? [filterData.gender] : [];
  }

  private getExcludeJobtitles(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let excludeJobTitles = filterData.jobTitleSelectedList?.length ? 
      filterData.jobTitleSelectedList?.filter(j => !!j.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key) : [];
    if (("talent-insights" === extraInfo?.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.jobTitleSelectedList?.length)
      excludeJobTitles =  excludeJobTitles.concat(redirectDataTI.jobTitleSelectedList.filter(j => !!j.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key));
    return _.uniq(excludeJobTitles);
  }

  private getExcludeLocations(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let excludeLocations = filterData.locationSelectedList?.length ? 
      filterData.locationSelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    if (("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.locationSelectedList?.length)
      excludeLocations =  excludeLocations.concat(redirectDataTI.locationSelectedList.filter(j => !!j.isExcluded).map(j => j.key));
    return _.uniq(excludeLocations);
  }

  // private getSelectedCompanies(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
  //   if(this.talentInsightsReqHandlerService.isCompanyInsightsTab)
  // }

  private getExcludeCompanies(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let excludeCompanies = filterData.companySelectedList?.length ? 
      filterData.companySelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    if (("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.companySelectedList?.length)
      excludeCompanies =  excludeCompanies.concat(redirectDataTI.companySelectedList.filter(j => !!j.isExcluded).map(j => j.key));
    return _.uniq(excludeCompanies);
  }

  private getExcludeIndustries(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let excludeIndustries = filterData.industrySelectedList?.length ? 
      filterData.industrySelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    if (("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.industrySelectedList?.length)
      excludeIndustries =  excludeIndustries.concat(redirectDataTI.industrySelectedList.filter(j => !!j.isExcluded).map(j => j.key));
    return _.uniq(excludeIndustries);
  }

  private getExcludSchoolLocation(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let excludeSchoolLocations = filterData.schoolLocationSelectedList?.length ? 
      filterData.schoolLocationSelectedList?.filter(j => !!j.isExcluded).map(j => j.key) : [];
    if (("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.schoolLocationSelectedList?.length)
    excludeSchoolLocations =  excludeSchoolLocations.concat(redirectDataTI.schoolLocationSelectedList.filter(j => !!j.isExcluded).map(j => j.key));
    return _.uniq(excludeSchoolLocations);
  }

  private getCompanySizes(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    const companySizeFilterData = filterData.companysize ? _.flatten(filterData.companysize) : [];
    const companySizeTI = ("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.companysize?.length ? _.flatten(redirectDataTI.companysize) : [];
    if (companySizeFilterData?.length) {
      return companySizeFilterData;
    }
    if (companySizeTI.length)
      return companySizeTI;
    return [];
  }

  private getBooleanQuery(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    // if ('talent-insights' === extraInfo.redirectSource && redirectDataTI?.keywordSelectedList?.length) {
    //   return redirectDataTI.keywordSelectedList.filter(Boolean).map(el => el.key);
    // }
    // if (this.filterDataHandlerService.isReportDetailsPage && filterData?.keywordSelectedList?.length) {
    //   return filterData.keywordSelectedList.filter(Boolean).map(el => el.key);
    // }
    return filterData.booleanquery ? filterData.booleanquery : "*";
  }

  private getExcludePastJobTitles(extraInfo: ExtraInfo,redirectDataTI: FilterParams, redirectDataCI: FilterParams, filterData: FilterParams) {
    let excludePastJobTitles = filterData?.pastJobTitleSelectedList?.length ? 
      filterData.pastJobTitleSelectedList?.filter(j => !!j.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key) : [];
    if (("talent-insights" === extraInfo.redirectSource || "company-insights" === extraInfo?.redirectSource) && redirectDataTI.pastJobTitleSelectedList?.length)
      excludePastJobTitles =  excludePastJobTitles.concat(redirectDataTI.pastJobTitleSelectedList.filter(j => !!j.isExcluded).map(j => j.isSuggested && !j.key.includes('.aisearch') ? `${j.key}.aisearch` : j.key));
    return _.uniq(excludePastJobTitles);
  }

  manadatoryApplicable(extraInfo: ExtraInfo) {
    return (
      extraInfo?.redirectSource == 'talent-insights' || 
      extraInfo?.redirectSource == 'company-insights'
    );
  }

  getTotalAggs(statusMap?: StatusMap) {
    if (!statusMap || _.isEmpty(statusMap)) {
      statusMap = this.getStatusMap(this.filterDataHandlerService.fetchFilterData);
    }

    const aggObservations : Observable<any>[] = [
      this.getAggregationReport('location'),
      this.getAggregationReport('skills'),
      this.getAggregationReport('currentyearsofexperience'),
      this.getAggregationReport('education'),
      this.getAggregationReport('school'),
      this.getAggregationReport('fieldofstudy'),
      this.getAggregationReport('seniority'),
      this.getAggregationReport('gender'),
      this.getAggregationReport('school_location'),
    ];
    if (statusMap.jobTitleStatus === CallStage.CURRENT_OR_PAST.toString()) aggObservations.push(this.getAggregationReport('jobtitle'));
    if (statusMap.jobTitleStatus === CallStage.CURRENT.toString()) aggObservations.push(this.getAggregationReport('current_jobtitle'));
    if (statusMap.jobTitleStatus === CallStage.PAST.toString()) aggObservations.push(this.getAggregationReport('pastjobtitle'));

    if (statusMap.companyStatus === CallStage.CURRENT_OR_PAST.toString())  aggObservations.push(this.getAggregationReport('company'));
    if (statusMap.companyStatus === CallStage.CURRENT.toString())  aggObservations.push(this.getAggregationReport('current_company'));
    if (statusMap.companyStatus === CallStage.PAST.toString())  aggObservations.push(this.getAggregationReport('pastcompany'));

    if (statusMap.industryStatus === CallStage.CURRENT_OR_PAST.toString()) aggObservations.push(this.getAggregationReport('industry'));
    if (statusMap.industryStatus === CallStage.CURRENT.toString()) aggObservations.push(this.getAggregationReport('current_industry'));
    if (statusMap.industryStatus === CallStage.PAST.toString()) aggObservations.push(this.getAggregationReport('pastindustry'));

    if (statusMap.yearsOfExperienceType === 'Total') aggObservations.push(this.getAggregationReport('totalyearsofexperience'));
    if (statusMap.yearsOfExperienceType === 'Average') aggObservations.push(this.getAggregationReport('averageyearsofexperience'));

    this.loaderEventService.emitLoaderObject(LoaderType.INSIGHTS);
    aggObservations.forEach(obs => obs.subscribe(res => this.emitInsightsObject(res)));
  }

  private getStatusMap(filterData: FilterParams) {
     return {
      jobTitleStatus : filterData.jobTitleStatus, 
      companyStatus : filterData.companyStatus,
      industryStatus : filterData.industryStatus,
      yearsOfExperienceType : filterData.yearsofexperience
    };
  }

  getTableByStatus(jobTitleStatus: string) {
    switch (jobTitleStatus) {
      case CallStage.CURRENT_OR_PAST.toString():
        return "jobtitle";
      case CallStage.PAST.toString():
        return "pastjobtitle";
      default:
        return "current_jobtitle";
    }
  }

  private createCompanyData(companies: any[]) {
    const CharForCurrentCompany = [];
    companies.forEach((x) => {
      let dataObj = {
        label: `${x.key.charAt(0).toUpperCase()}${x.key.slice(1)}`,
        value: x.doc_count,
        tooltext: `${x.key.charAt(0).toUpperCase()}${x.key.slice(
          1
        )} <br> Talent count: ${x.doc_count}`,
        companylogo: x.companylogo,
        name: x.name,
        industry: x.industry,
        location: x.location,
        size: x.size,
        website: x.website,
        linkedin_url: x.linkedin_url,
        tags: x.tags,
      };
      CharForCurrentCompany.push(dataObj);
    });

    const dataSourceCurrentCompany = {
      type: "column2d",
      dataFormat: "json",
      height: 14,
      width: 14,
      chart: {
        renderAt: "chart-container",
        paletteColors: "#5c63a2",
        showPercentValues: "1",
        yAxisNameFontColor: "#2C2C2C",
        legendPosition: "right",
        theme: "fusion",
        showValues: "1",
        labelFont: "Poppins",
        legendItemFont: "Poppins",
        legendItemFontSize: "12",
      },
      data: CharForCurrentCompany,
    };
    return dataSourceCurrentCompany;
  }

  createWorkCountryData(response) {
    const CharForworkCountry = [];
    CharForworkCountry.push(["Location", "Talent count"]);
    response?.location.slice(0, 10).forEach((x) => {
      const dataObj = [
        `${x.key.charAt(0).toUpperCase()}${x.key.slice(1)}`,
        x.doc_count,
      ];
      CharForworkCountry.push(dataObj);
    });
    return CharForworkCountry;
  }

  private createSeniorityData(response) {
    const CharForSeniority = [];
    response?.seniority?.forEach((x) => {
      let dataObj = {
        label: `${x.key.charAt(0).toUpperCase()}${x.key.slice(1)}`,
        value: x.doc_count,
        tooltext: `${x.key.charAt(0).toUpperCase()}${x.key.slice(
          1
        )} <br> Talent count: ${x.doc_count}`,
        link: `${x.label}  ${x.value}`,
      };
      CharForSeniority.push(dataObj);
    });

    let height = "500",
      width = "500";
    if (CharForSeniority.length <= 1) {
      height = "400";
      width = "400";
    }
    const dataSourceSeniority = {
      type: "doughnut2d",
      renderAt: "talent_gender",
      dataFormat: "json",
      height: height,
      width: width,
      chart: {
        paletteColors: "#F4AB33, #EC7176, #5C63A2, #1B4E6B",
        subCaption: "",
        use3DLighting: "0",
        showPercentValues: "1",
        decimals: "0",
        yAxisNameFontColor: "#2C2C2C",
        animateClockwise: "1",
        legendPosition: "bottom",
        theme: "fusion",
        labelFont: "Poppins",
        legendItemFont: "Poppins",
        legendItemFontSize: "12",
      },
      data: CharForSeniority,
    };
    return dataSourceSeniority;
  }

  private createCurrentExperience(response, type) {
    let ChartForCurrentExperience = [];
    response && response[type]?.forEach((x) => {
      let dataObj = {
        label: `${x.key}`,
        value: x.doc_count,
        tooltext: `${x.key} <br> Talent count: ${x.doc_count}`,
        link: `${x.label}  ${x.value}`,
      };
      ChartForCurrentExperience.push(dataObj);
    });
    ChartForCurrentExperience = ChartForCurrentExperience.filter(
      (el) => !!el.value
    )?.slice(0, 40);

    const dataSourceCurrentExperience = {
      type: "column2d",
      dataFormat: "json",
      chart: {
        paletteColors: "#B4D0FD;",
        captionFontColor: "#F4AB33",
        showPercentValues: "1",
        yAxisNameFontColor: "#F4AB33",
        theme: "fusion",
        valuePosition: "right",
        showYAxisValues: "1",
      },
      data: ChartForCurrentExperience,
    };
    return dataSourceCurrentExperience;
  }

  private createExpInCurrentRole(response: any) {
    let ChartForExpInCurrentRole = [];
    response?.currentyearsofexperience?.forEach((x) => {
      let dataObj = {
        label: `${x.key}`,
        value: x.doc_count,
        tooltext: `${x.key} <br> Talent count: ${x.doc_count}`,
        link: `${x.label}  ${x.value}`,
      };
      ChartForExpInCurrentRole.push(dataObj);
    });
    ChartForExpInCurrentRole = ChartForExpInCurrentRole.filter(
      (el) => !!el.value
    )?.slice(0, 40);

    const dataSourceExpInCurrentRole = {
      type: "column2d",
      dataFormat: "json",
      chart: {
        paletteColors: "#B4D0FD;",
        captionFontColor: "#F4AB33",
        showPercentValues: "1",
        yAxisNameFontColor: "#F4AB33",
        theme: "fusion",
        valuePosition: "right",
        showYAxisValues: "1",
      },
      data: ChartForExpInCurrentRole,
    };
    return dataSourceExpInCurrentRole;
  }

  createDataSourceGender(resp) {
    const response = Object.assign({}, resp);
    const ChartForGender = [];
    response?.gender?.forEach((x) => {
      if (x.key == "male" || x.key == "female") {
        let dataObj = {
          label: x.key == "male" ? "Men" : "Women",
          value: x.doc_count,
          tooltext: `${x.key == "male" ? "Men" : "Women"} <br> Talent count: ${
            x.doc_count
          }`,
          link: `${x.label}  ${x.value}`,
        };
        ChartForGender.push(dataObj);
      }
    });

    const dataSourceGender = {
      type: "doughnut2d",
      renderAt: "talent_gender",
      dataFormat: "json",
      height: "400",
      width: "400",
      chart: {
        paletteColors: "#F4AB33, #EC7176, #5C63A2, #1B4E6B",
        subCaption: "",
        use3DLighting: "0",
        showPercentValues: "1",
        decimals: "0",
        yAxisNameFontColor: "#2C2C2C",
        animateClockwise: "1",
        legendPosition: "bottom",
        theme: "fusion",
        labelFont: "Poppins",
        legendItemFont: "Poppins",
        legendItemFontSize: "12",
      },
      data: ChartForGender,
    };
    return Object.assign({}, dataSourceGender);
  }

  /**
   * Legacy method. Replaced with V2
   */
  getTalentInsightsRedirectData() {
    let redirectDataTS: FilterParams;
    try {
      redirectDataTS = JSON.parse(this.localStorageService.getItem(GeneralConstants.LS_KEYS.TALENT_FILTERS));
    } catch (error) {
      console.log('error while parsing reidrect data for talent insights', redirectDataTS);
    }
    return redirectDataTS;
  }

  getTalentInsightsRedirectDataV2() {
    let redirectDataTI: FilterParams;
    try {
      const uniqueHash = this.activatedRoute.snapshot.queryParamMap.get(GeneralConstants.LS_KEYS.UNIQUE_HASH);
      const key = `${GeneralConstants.LS_KEYS.TALENT_FILTERS}-${uniqueHash}`;
      redirectDataTI = JSON.parse(this.sessionStorageService.getItem(key));
    } catch (error) {
      console.log('error while parsing reidrect data using hash key for talent insights', error);
    }
    return redirectDataTI;
  }

  getTalentInsightsMandatoryData() : any {
    let redirectData = {};
    try {
      const uniqueHash = this.activatedRoute.snapshot.queryParamMap.get(GeneralConstants.LS_KEYS.UNIQUE_HASH );
      const key = `talentfilters-${uniqueHash}`;
      redirectData = JSON.parse(this.sessionStorageService.getItem(key));
    } catch (error) {
      console.log('error while fetching mandatory data for TI', error);
    }
    return redirectData;
  }

  getCompanyInsightsRedirection() {
    let redirectDataTS: FilterParams;
    try {
      redirectDataTS = JSON.parse(this.localStorageService.getItem(GeneralConstants.LS_KEYS.COMPANY_FILTERS));
    } catch (error) {
      console.log('error while parsing reidrect data for talent insights', redirectDataTS);
    }
    return redirectDataTS;
  }

  getCompanyInsightsRedirectDataV2() {
    let redirectDataTI: FilterParams;
    try {
      const uniqueHash = this.activatedRoute.snapshot.queryParamMap.get(GeneralConstants.LS_KEYS.UNIQUE_HASH_COMPANY);
      const key = `${GeneralConstants.LS_KEYS.COMPANY_FILTERS}-${uniqueHash}`;
      redirectDataTI = JSON.parse(this.sessionStorageService.getItem(key));
    } catch (error) {
      console.log('error while parsing reidrect data using hash key for talent insights', error);
    }
    return redirectDataTI;
  }

  getCompanyInsightsMandatoryData() : any {
    let redirectData = {};
    try {
      const uniqueHash = this.activatedRoute.snapshot.queryParamMap.get(GeneralConstants.LS_KEYS.UNIQUE_HASH_COMPANY );
    const key = `companyfilters-${uniqueHash}`;
      redirectData = JSON.parse(this.sessionStorageService.getItem(key));
    } catch (error) {
      console.log('error while fetching mandatory data for TI', error);
    }
    return redirectData;
  }


}
